import { FolderKanban } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { PROJECT_STATUSES } from "./OrgDetailPage";
import { useAdminOrgs, useAdminProjects } from "@/api/hooks/admin";
import type { ProjectStatus } from "@/api/types";
import { usePageCrumbs } from "@/app/layouts/shellContext";
import { EmptyState } from "@/components/EmptyState";
import { ErrorNotice } from "@/components/ErrorNotice";
import { Field } from "@/components/Field";
import { PageHeader } from "@/components/PageHeader";
import { PageLoading } from "@/components/Spinner";
import { StatusPill } from "@/components/StatusPill";
import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { formatDate } from "@/lib/dates";

/** Staff: all projects with status and organisation filters (kept in the URL). */
export function AdminProjectsPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") ?? "") as ProjectStatus | "";
  const orgId = params.get("org") ?? "";
  const projects = useAdminProjects({ status: status || undefined, orgId: orgId || undefined });
  const requests = useAdminProjects({ status: "requested" });
  const orgs = useAdminOrgs("");
  usePageCrumbs([{ label: t("common.nav.admin") }, { label: t("admin.projects.title") }]);

  const setFilter = (key: "status" | "org", value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const waiting = requests.data?.length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("admin.projects.title")} description={t("admin.projects.intro")} />
      {waiting > 0 && status !== "requested" ? (
        <Banner
          tone="warning"
          action={
            <Button variant="secondary" size="sm" onClick={() => setFilter("status", "requested")}>
              {t("admin.projects.showRequests")}
            </Button>
          }
        >
          {t("admin.projects.requestsCount", { count: waiting })}
        </Banner>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2 lg:max-w-2xl">
        <Field label={t("admin.projects.filterStatus")}>
          {(p) => (
            <Select {...p} className="w-full" value={status} onChange={(e) => setFilter("status", e.target.value)}>
              <option value="">{t("admin.projects.allStatuses")}</option>
              {PROJECT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`project.status.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t("admin.projects.filterOrg")}>
          {(p) => (
            <Select {...p} className="w-full" value={orgId} onChange={(e) => setFilter("org", e.target.value)}>
              <option value="">{t("admin.projects.allOrgs")}</option>
              {(orgs.data ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {projects.isPending ? <PageLoading /> : null}
      {projects.isError ? <ErrorNotice error={projects.error} /> : null}
      {projects.data && projects.data.length === 0 ? (
        <EmptyState
          icon={<FolderKanban className="h-6 w-6" />}
          title={t("admin.projects.empty")}
          description={t("admin.projects.emptyBody")}
        />
      ) : null}
      {projects.data && projects.data.length > 0 ? (
        <Table aria-label={t("admin.projects.title")}>
          <THead>
            <TR>
              <TH>{t("admin.projects.colProject")}</TH>
              <TH>{t("admin.projects.colOrg")}</TH>
              <TH>{t("admin.projects.colStatus")}</TH>
              <TH>{t("admin.projects.colType")}</TH>
              <TH>{t("admin.projects.colCreated")}</TH>
            </TR>
          </THead>
          <TBody>
            {projects.data.map((p) => (
              <TR key={p.id}>
                <TD className="font-medium">
                  <span className="flex flex-wrap items-center gap-2">
                    <Link to={`/admin/projects/${p.id}`}>{p.name}</Link>
                    {p.status === "requested" ? <Badge variant="warning">{t("admin.newRequest")}</Badge> : null}
                  </span>
                </TD>
                <TD>
                  <Link to={`/admin/orgs/${p.organisation_id}`} className="text-fg">
                    {p.organisation_name}
                  </Link>
                </TD>
                <TD>
                  <StatusPill status={p.status} />
                </TD>
                <TD className="text-fg-muted">
                  {p.project_type
                    ? p.project_type === "other" && p.project_type_other
                      ? p.project_type_other
                      : t(`project.type.${p.project_type}`)
                    : "–"}
                </TD>
                <TD className="whitespace-nowrap text-fg-muted">{formatDate(p.created_at)}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      ) : null}
    </div>
  );
}
